import {
  WBFlex,
  WBIcon,
  WBAutocomplete,
  WBTextFieldV2,
  WBTypography,
  WBBox,
  WBMenuItem,
} from '@admiin-com/ds-web';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { styled, useTheme } from '@mui/material';
import { DATE_FORMATS } from '@admiin-com/ds-common';
import { t } from 'i18next';

export type InvoiceFrequencyDatePickerProps = {
  onChange: (value: string) => void;
  variant?: 'standard' | 'outlined' | 'filled';
  error?: boolean;
  helperText?: string;
  value?: string;
  disabled?: boolean;
  placeholder?: string;
  type?: 'date' | 'datetime-local';
  label?: string;
  fullWidth?: boolean;
  onBlur?: () => void;
  name?: string;
  showRecurringOptions?: boolean;
  startDate?: string; // For calculating number of payments
  frequency?: string; // For calculating number of payments
};

const today = DateTime.now();

// Function to format date ranges for recurring invoice end dates
const formatDateRange = (months: number) => {
  const date = today.plus({ months });
  return {
    value: date.toFormat(DATE_FORMATS.BACKEND_DATE),
    label: `${
      months === 3
        ? t('threeMonths', { ns: 'taskbox' })
        : months === 6
        ? t('sixMonths', { ns: 'taskbox' })
        : months === 12
        ? t('oneYear', { ns: 'taskbox' })
        : t('twoYears', { ns: 'taskbox' })
    } (${date.toFormat('dd MMM yyyy')})`,
    isCustom: false,
  };
};

const daysToString = (days: number) =>
  days === 0
    ? t('today', { ns: 'taskbox' })
    : t('days', { ns: 'taskbox', day: days });

// Standard date options for regular date picker
const standardDateOptions = [0, 7, 14, 30].map((days) => {
  const date = today.plus({ days });
  return {
    value: date.toFormat(DATE_FORMATS.BACKEND_DATE),
    label: `${daysToString(days)} (${date.toFormat('dd MMM')})`,
    isCustom: false,
  };
});

// Recurring date options for end date picker
const recurringDateOptions = [3, 6, 12, 24].map((months) =>
  formatDateRange(months)
);

// Function to calculate the number of payments between two dates based on frequency
export const calculateNumberOfPayments = (
  startDate: string,
  endDate: string,
  frequency: string
) => {
  if (!startDate || !endDate) return 0;

  try {
    const start = DateTime.fromFormat(startDate, DATE_FORMATS.BACKEND_DATE);
    const end = DateTime.fromFormat(endDate, DATE_FORMATS.BACKEND_DATE);

    if (!start.isValid || !end.isValid) return 0;

    // Calculate the time difference based on frequency
    const diffInDays = Math.ceil(end.diff(start, 'days').days);

    switch (frequency) {
      case 'WEEKLY':
        return Math.ceil(diffInDays / 7);
      case 'FORTNIGHTLY':
        return Math.ceil(diffInDays / 14);
      case 'MONTHLY':
        // Calculate months difference
        return Math.ceil(end.diff(start, 'months').months);
      case 'QUARTERLY':
        // Calculate quarters difference (3 months)
        return Math.ceil(end.diff(start, 'months').months / 3);
      case 'ANNUALLY':
        // Calculate years difference
        return Math.ceil(end.diff(start, 'years').years);
      default:
        return Math.ceil(diffInDays / 30); // Default to monthly
    }
  } catch (error) {
    return 0;
  }
};

const renderDate = (date: string) => {
  if (!date) return '';

  try {
    const parsedDate = DateTime.fromFormat(date, DATE_FORMATS.BACKEND_DATE);
    if (!parsedDate.isValid) return date;

    const dueDateFormat = parsedDate.toFormat(DATE_FORMATS.DUE_DATE);

    // Calculate days difference using Australia/Sydney timezone consistently
    const targetDate = parsedDate.setZone('Australia/Sydney').startOf('day');
    const todaySydney = DateTime.now()
      .setZone('Australia/Sydney')
      .startOf('day');
    const days = Math.round(targetDate.diff(todaySydney, 'days').days);

    return `${dueDateFormat} (${daysToString(days)})`;
  } catch (error) {
    return date;
  }
};

// Function to parse manual date input (DD-MM-YYYY)
const parseManualDate = (input: string): string | null => {
  try {
    // Try to parse DD-MM-YYYY format
    const parsed = DateTime.fromFormat(input, 'dd-MM-yyyy');
    if (parsed.isValid) {
      return parsed.toFormat(DATE_FORMATS.BACKEND_DATE);
    }

    // Try to parse DD/MM/YYYY format as fallback
    const parsedSlash = DateTime.fromFormat(input, DATE_FORMATS.USER_DATE);
    if (parsedSlash.isValid) {
      return parsedSlash.toFormat(DATE_FORMATS.BACKEND_DATE);
    }

    return null;
  } catch {
    return null;
  }
};

export const InvoiceFrequencyDatePicker = React.forwardRef<
  any,
  InvoiceFrequencyDatePickerProps
>((props: InvoiceFrequencyDatePickerProps, ref) => {
  const {
    onChange,
    onBlur,
    variant = 'standard',
    error,
    helperText,
    value = '',
    name,
    disabled,
    placeholder,
    fullWidth = true,
    label,
    showRecurringOptions = false,
    startDate,
    frequency,
  } = props;

  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const theme = useTheme();

  // Determine which options to show based on showRecurringOptions prop
  const dateOptions = showRecurringOptions
    ? recurringDateOptions
    : standardDateOptions;

  const allOptions = [
    ...dateOptions,
    {
      value: 'custom',
      label: t('customDate', { ns: 'taskbox' }),
      isCustom: true,
    },
  ];

  const handleOnChange = (newValue: any) => {
    if (
      newValue &&
      typeof newValue === 'object' &&
      newValue !== null &&
      (newValue as any).isCustom
    ) {
      // Handle custom date selection - open date picker
      inputRef.current?.click();
      return;
    } else if (newValue && typeof newValue === 'object' && newValue !== null) {
      // Handle predefined option selection
      onChange((newValue as any).value);
    } else if (typeof newValue === 'string') {
      // Handle manual text input
      const parsedDate = parseManualDate(newValue);
      if (parsedDate) {
        onChange(parsedDate);
      } else {
        // If parsing fails, pass the raw value
        onChange(newValue);
      }
    } else if (newValue === null) {
      onChange('');
    }
  };

  // Calculate number of payments if relevant props are provided
  const numberOfPayments = React.useMemo(() => {
    if (showRecurringOptions && startDate && value && frequency) {
      return calculateNumberOfPayments(startDate, value, frequency);
    }
    return 0;
  }, [showRecurringOptions, startDate, value, frequency]);

  const [open, setOpen] = React.useState(false);

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const displayValue = React.useMemo(() => {
    if (!value) return null;

    try {
      if (!open) {
        return renderDate(value);
      } else {
        const parsedDate = DateTime.fromFormat(
          value,
          DATE_FORMATS.BACKEND_DATE
        );
        return parsedDate.isValid ? parsedDate.toFormat('dd-MM-yyyy') : value;
      }
    } catch (error) {
      return value;
    }
  }, [value, open]);

  return (
    <>
      <WBAutocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          handleBlur();
        }}
        ref={ref}
        freeSolo
        disabled={disabled}
        value={displayValue}
        options={allOptions}
        onInputChange={(event, newValue) => {
          const parsedDate = parseManualDate(newValue);
          if (parsedDate) {
            handleOnChange(parsedDate);
          }
        }}
        popupIcon={<WBIcon name="ChevronDown" size={1.3} color={'black'} />}
        fullWidth={fullWidth}
        getOptionLabel={(option: any) => {
          if (typeof option === 'string') return option;
          return option?.label || '';
        }}
        isOptionEqualToValue={() => false}
        onChange={(event, newValue, reason) => {
          handleOnChange(newValue);
        }}
        renderInput={(params) => (
          <WBTextFieldV2
            {...params}
            name={name}
            label={label}
            // variant={variant}
            error={error}
            helperText={helperText}
            onBlur={handleBlur}
            placeholder={
              placeholder ||
              t('selectDate', { ns: 'taskbox', defaultValue: 'Select date' }) +
                ' (dd-MM-yyyy)'
            }
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <WBBox sx={{ marginRight: theme.spacing(1) }}>
                  <WBIcon
                    color={theme.palette.grey[500]}
                    name="Calendar"
                    size={2}
                  />
                </WBBox>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          return (
            <StyledOption
              {...props}
              value={option.value}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                handleOnChange(option);
                setOpen(false);
                handleBlur();
              }}
            >
              {option.isCustom ? (
                <WBFlex alignItems="center">
                  <WBIcon name="Calendar" size={2} color={'black'} />
                  <WBTypography ml={1}>{option.label}</WBTypography>
                </WBFlex>
              ) : (
                <WBFlex justifyContent={'space-between'} width={'100%'}>
                  <WBTypography>{option.label.split(' (')[0]}</WBTypography>
                  <WBTypography>
                    {option.label.split(' (')[1]?.replace(')', '')}
                  </WBTypography>
                </WBFlex>
              )}
            </StyledOption>
          );
        }}
      />
      {showRecurringOptions && numberOfPayments > 0 && value && (
        <WBTypography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          {t('calculatedNumberOfPayments', {
            ns: 'taskbox',
            count: numberOfPayments,
          })}
        </WBTypography>
      )}
      <MobileDatePicker
        sx={{ display: 'none' }}
        inputRef={inputRef}
        onAccept={(date: any) => {
          if (date && date.isValid) {
            handleOnChange(date.toFormat(DATE_FORMATS.BACKEND_DATE));
            handleBlur();
          }
        }}
      />
    </>
  );
});

const StyledOption = styled(WBMenuItem)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));

export default InvoiceFrequencyDatePicker;
